// src/services/cpf-external/factory.js
// *******************************************************************************
// CPF Provider Factory - Single provider (expandable in future)
// *******************************************************************************
// Factory pattern for CPF lookup with provider abstraction
// Currently only Jarvisly (backend), but ready for future providers
// Based on backend pattern: src/modules/integrations/factory-helpers.js
// *******************************************************************************

import jarvislyAdapter from './jarvisly/adapter.js';
import { createProviderFactory } from '../address/factory-helpers.js';

/**
 * CPF Provider registry
 * Currently only Jarvisly, but prepared for future expansion
 * Future providers could include:
 * - ReceitaWS (free API)
 * - BrasilAPI (future CPF endpoint)
 * - Other third-party CPF validators
 */
const CPF_REGISTRY = {
  jarvisly: { name: 'jarvisly', adapter: jarvislyAdapter },
};

/**
 * Creates CPF provider instance with automatic fallback
 *
 * @param {Object} [options] - Factory options
 * @param {string|Array<string>} [options.provider] - Provider(s) to use
 *   - If not specified: uses default provider (jarvisly)
 *   - If string: single provider name
 *   - If array: ordered provider names (for future multi-provider support)
 *
 * @returns {Object} - Provider instance
 * @returns {Array<string>} return.chain - Active provider chain
 * @returns {Object} return.adapter - Composite adapter with fallback logic
 *
 * @example
 * // Use default provider (jarvisly)
 * const { adapter } = getCpfProvider();
 * const result = await adapter.searchCpf({ cpf: '12345678900' });
 *
 * @example
 * // Explicitly specify provider
 * const { adapter } = getCpfProvider({ provider: 'jarvisly' });
 *
 * @example
 * // Future: Multiple providers with fallback
 * const { adapter } = getCpfProvider({ provider: ['jarvisly', 'receitaws'] });
 */
export const getCpfProvider = createProviderFactory({
  registry: CPF_REGISTRY,
  methods: ['searchCpf'],
});

export default {
  getCpfProvider,
};
